/**
 * Phase 8 T-06 §7 F / G + T-06c §21.B — Smart Match canvas match card.
 *
 * Condensed Wrangle-style card used inside `MatchStackColumn`. Replaces
 * `SmartMatchResultCard` (Phase-7) for the canvas view only; the legacy card
 * stays wired to the `MatchesTab` grid so the upload flows are unchanged.
 *
 * Per §21.B the bottom actions are tab-aware:
 *   - `variant="sourcing"`     → `[← Hide]` (red-soft) + `[Shortlist →]` / `[Save →]`
 *                                 (label from `shortlistLabelFor`).
 *   - `variant="shortlisted"`  → `[← Mark as seen]` (neutral) + `[Remove →]` (red-soft).
 *   - `variant="hidden"`       → single full-width `[Restore]` (emerald outline).
 *
 * The active/focused card gets a 2 px emerald left border per §7 G. All
 * actions optimistically bubble up to the parent; the parent owns the
 * existing service calls so automation hooks fire exactly where they did
 * before T-06c.
 */

import { forwardRef, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  Check,
  ChevronRight,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Linkedin,
  Loader2,
  MapPin,
  RotateCcw,
  Tag,
  Undo2,
  Users,
} from 'lucide-react';
import type { MatchResult } from '../types';
import { ScoreChip } from '@shared/components/ui/ScoreChip';
import {
  bandOf,
  BAND_LABEL,
  clientNameFromMatch,
  jobTitleFromMatch,
} from '../lib/matchDisplay';
import {
  shortlistLabelFor,
  shouldOpenCollectionsPicker,
} from '../lib/shortlistSemantic';
import type { SmartMatchCollectionRef } from '../lib/smartMatchThreads';
import { CollectionsPicker } from './CollectionsPicker';

export type MatchCardVariant = 'sourcing' | 'shortlisted' | 'hidden';

export interface MatchCardProps {
  match: MatchResult;
  /**
   * Other MatchResults for the same candidate (N×M cross-JD
   * alternates). Sorted by `composite_score` desc, already excludes
   * the current row. Rendered as the "Also a fit for" footer so the
   * user can answer "what else might this candidate work for?" without
   * opening the rail. Empty / undefined hides the section entirely.
   */
  alternateMatches?: MatchResult[];
  /** Which tab the card lives in — drives the bottom-action set. */
  variant?: MatchCardVariant;
  /** Controlled focus — usually set by keyboard nav or click. */
  focused?: boolean;
  /** Whether this card has already been shortlisted in the current session. */
  shortlisted?: boolean;
  /**
   * Shortlisted-tab seen tracker. When `true` the card shows a green
   * "Seen" badge in the header and the `Mark as seen` button flips to
   * `Mark as unseen` so the state is reversible (post user-feedback,
   * 21-Apr — issues b + d).
   */
  viewed?: boolean;
  /**
   * 1-based dense rank by `composite_score DESC` within the current
   * filtered + sorted result set. Rendered as a small white disc pinned
   * to the top-left of the card per `docs/smart-match/12_DATAFLOW §13.2`.
   * When `undefined` the rank disc is suppressed — callers on the
   * Shortlisted / Hidden tabs intentionally omit it because the order
   * there is "when the user acted on the card", not "how good the match
   * is".
   */
  rank?: number;
  /** Whether the Shortlist/Save action is currently resolving. */
  shortlistBusy?: boolean;
  /** Whether the Hide / Mark-as-seen action is currently resolving. */
  hideBusy?: boolean;
  /** Whether the Remove / Restore action is currently resolving. */
  removeBusy?: boolean;
  /**
   * Thread-level collection state. `null` → emerald button reads `Save →`;
   * `{ id, name }` → emerald button reads `Shortlist →`. See §21.B.
   */
  collection?: SmartMatchCollectionRef | null;
  onFocus?: (m: MatchResult) => void;
  onOpen?: (m: MatchResult) => void;
  onShortlist?: (m: MatchResult) => void;
  onHide?: (m: MatchResult) => void;
  /**
   * Called when the user picks a collection from the inline picker (§21.E).
   * The parent owns thread state — once the collection is attached the
   * emerald label flips from `Save →` to `Shortlist →` via
   * `shortlistLabelFor`. The card will follow the pick with a `onShortlist`
   * call so the candidate is saved into the freshly-attached collection.
   */
  onAttachCollection?: (collection: SmartMatchCollectionRef) => void;
  /** Shortlisted-tab: move the card back to sourcing. */
  onMarkAsSeen?: (m: MatchResult) => void;
  /** Shortlisted-tab: remove from the shortlist (goes back to Sourcing). */
  onRemove?: (m: MatchResult) => void;
  /** Hidden-tab: restore the card to sourcing. */
  onRestore?: (m: MatchResult) => void;
}

function channelIconFor(ch: string): typeof Calendar {
  const n = ch.toLowerCase().replace(/[^a-z]/g, '');
  if (n === 'linkedin') return Linkedin;
  if (n === 'naukri' || n === 'indeed' || n === 'jobboard') return Briefcase;
  if (n === 'referral') return Users;
  if (n === 'agency') return Building2;
  return Tag;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarUrlFor(name: string, candidateId?: string): string {
  const seed = candidateId || name.replace(/\s+/g, '');
  const hash = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `https://i.pravatar.cc/150?u=${encodeURIComponent(seed + hash)}`;
}

/**
 * Bolt-inspired icon-fact row — one line with a tinted icon on the
 * left and a truncated label. Centralising the markup keeps the
 * experience/location/remote triplet visually consistent even when
 * only one field is present.
 */
function Fact({
  Icon,
  text,
  title,
}: {
  Icon: typeof Calendar;
  text: string;
  title?: string;
}): JSX.Element {
  return (
    <div
      className="flex items-center gap-1.5 text-[11px]"
      style={{ color: 'var(--vscode-text-secondary)' }}
      title={title ?? text}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{text}</span>
    </div>
  );
}

export const MatchCard = forwardRef<HTMLDivElement, MatchCardProps>(function MatchCard(
  {
    match: m,
    alternateMatches = [],
    variant = 'sourcing',
    focused = false,
    shortlisted = false,
    viewed = false,
    rank,
    shortlistBusy = false,
    hideBusy = false,
    removeBusy = false,
    collection = null,
    onFocus,
    onOpen,
    onShortlist,
    onHide,
    onAttachCollection,
    onMarkAsSeen,
    onRemove,
    onRestore,
  },
  ref,
) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const onPrimaryActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // §21.B + §21.E: when there's no collection attached, the emerald
    // button opens the picker instead of saving. After the user picks a
    // collection the parent will re-render with `collection != null` and a
    // subsequent click will go straight to `onShortlist`.
    if (shouldOpenCollectionsPicker(collection)) {
      setPickerOpen(true);
      return;
    }
    onShortlist?.(m);
  };
  const name = m.candidate?.full_name ?? 'Unknown candidate';
  const role = m.candidate?.current_title ?? m.candidate?.current_company ?? '—';
  // Normalise score — legacy KPI rows stored on a 0-1 scale otherwise
  // collapse into the lowest tier. `ScoreChip` also auto-scales but we
  // need the same value for `bandOf()` so the label + chip agree.
  const rawScore = Number.isFinite(m.composite_score)
    ? Number(m.composite_score)
    : 0;
  const score100 = Math.round(
    Math.max(0, Math.min(100, rawScore > 1 ? rawScore : rawScore * 100)),
  );
  const band = bandOf(score100);
  const bandLabel = BAND_LABEL[band].label;
  // For N×M multi-JD search, the per-row job is stamped with its JD
  // title + client name (see `promptSearch.toMatchResult`). These are
  // always safe to read — fall back to clientNameFromMatch/jobTitleFromMatch
  // for legacy rows.
  const jobTitle = jobTitleFromMatch(m);
  const clientName = clientNameFromMatch(m);
  const hasJobContext = jobTitle !== 'Role TBD' || clientName !== 'Client TBD';

  /**
   * 21-Apr-26 card refresh — Bolt-style fact strip.
   *
   * The previous `Very important / Important` grids surfaced correct
   * information but looked cluttered next to the reference the user
   * shared (Apply-for-jobs dashboard). We now split the same inputs
   * into three clearer buckets:
   *
   *   - `facts`           → icon rows (experience / location / remote)
   *   - `matchedSkills`   → green tag pills (top 6 to avoid wrap spam)
   *   - `missingSkills`   → muted-red tag pills (top 4)
   *
   * Dimension scores (semantic_similarity, experience_alignment,
   * location_fit, budget_fit) are intentionally collapsed into the
   * composite chip + the right-rail explainer — they distracted more
   * than they informed inside a 300 px card.
   */
  const facts = useMemo(() => {
    const out: Array<{ key: string; Icon: typeof Calendar; text: string; title?: string }> = [];
    const years =
      m.candidate?.total_experience_years ??
      m.candidate?.years_exp ??
      null;
    if (years != null && Number.isFinite(years)) {
      out.push({ key: 'exp', Icon: Calendar, text: `${Math.round(Number(years))}+ yrs` });
    }
    if (m.candidate?.current_company) {
      out.push({
        key: 'co',
        Icon: Briefcase,
        text: String(m.candidate.current_company),
      });
    }
    if (m.candidate?.location) {
      out.push({ key: 'loc', Icon: MapPin, text: String(m.candidate.location) });
    }
    const prefs = (m.candidate?.preferred_locations ?? []) as string[] | null;
    if (prefs && prefs.some((p) => /remote/i.test(String(p)))) {
      out.push({ key: 'remote', Icon: Globe, text: 'Open to remote' });
    }
    return out.slice(0, 4);
  }, [m.candidate]);

  const matchedSkills = useMemo(
    () => (m.matched_skills ?? []).slice(0, 6),
    [m.matched_skills],
  );
  const missingSkills = useMemo(
    () => (m.missing_skills ?? []).slice(0, 4),
    [m.missing_skills],
  );

  const sources = useMemo(() => {
    const out: Array<{ key: string; label: string; Icon: typeof Linkedin }> = [];
    if (m.candidate?.linkedin_url) out.push({ key: 'li', label: 'LinkedIn', Icon: Linkedin });
    if (m.candidate?.base_resume_url) out.push({ key: 'rs', label: 'Resume', Icon: FileText });
    // Show the sourcing channel (where this candidate was found/imported from).
    // Skip deduplicating against the LinkedIn profile badge — "sourced via LinkedIn"
    // and "has a LinkedIn profile" are distinct facts worth surfacing separately,
    // but if there's no profile URL yet (e.g. Naukri import) this is the only badge.
    const ch = m.candidate?.source_channel;
    if (ch && typeof ch === 'string' && ch.trim()) {
      const isDuplicateLi =
        ch.toLowerCase() === 'linkedin' && Boolean(m.candidate?.linkedin_url);
      if (!isDuplicateLi) {
        out.push({
          key: 'ch',
          label: ch.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          Icon: channelIconFor(ch),
        });
      }
    }
    return out;
  }, [m.candidate?.linkedin_url, m.candidate?.base_resume_url, m.candidate?.source_channel]);

  const rootClasses = [
    'group relative flex flex-col gap-2 rounded-[10px] border p-3 text-left transition-colors',
    focused ? 'bg-[var(--ggj-accent-soft,#D1FAE5)]/30' : 'bg-[var(--vscode-bg-elevated)] hover:bg-[var(--vscode-bg-hover)]',
  ].join(' ');

  return (
    <div
      ref={ref}
      role="article"
      tabIndex={-1}
      aria-selected={focused}
      aria-label={`Match card for ${name}`}
      onClick={() => onFocus?.(m)}
      className={rootClasses}
      style={{
        borderColor: focused
          ? 'var(--ggj-accent,#10B981)'
          : 'var(--vscode-border-primary)',
        borderLeftWidth: focused ? 2 : 1,
      }}
    >
      {/*
       * Header row — avatar + name + (rank + seen badge + score chip + open).
       * Rank now sits in the RIGHT cluster next to the score chip per
       * 21-Apr-26 feedback ("move rank on right side"). Keeping it in
       * the header right-group means rank + score read as one unit:
       * "Rank #1 · 33%".
       */}
      <div className="flex items-start gap-2">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-emerald-400/30"
          style={{
            background: 'var(--ggj-accent-soft,#D1FAE5)',
          }}
          aria-hidden
        >
          <img
            src={m.candidate?.avatar_url || avatarUrlFor(name, m.candidate_id)}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<span class="text-[12px] font-semibold uppercase" style="color: #065F46">${initialsOf(name)}</span>`;
              }
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-[13px] font-semibold"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            {name}
          </p>
          <p
            className={`truncate text-[11px] ${BAND_LABEL[band].color}`}
            aria-label={`Match band ${bandLabel}`}
          >
            {bandLabel}
          </p>
          <p
            className="truncate text-[11px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
            title={role}
          >
            {role}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {rank != null && variant === 'sourcing' ? (
            <span
              aria-label={`Rank ${rank} by composite score`}
              title={`Rank #${rank}`}
              className="inline-flex h-5 shrink-0 items-center rounded-full px-1.5 text-[10px] font-semibold leading-none tabular-nums"
              style={{
                background: 'var(--ggj-accent-soft,#D1FAE5)',
                color: '#065F46',
                border:
                  '1px solid color-mix(in srgb, var(--ggj-accent,#10B981) 35%, transparent)',
              }}
            >
              Rank #{rank}
            </span>
          ) : null}
          {viewed && variant === 'shortlisted' ? (
            <span
              className="inline-flex h-5 items-center gap-1 rounded-full px-1.5 text-[10px] font-semibold"
              style={{
                background: 'var(--ggj-accent-soft,#D1FAE5)',
                color: '#065F46',
              }}
              title="You have marked this candidate as seen"
              aria-label="Marked as seen"
            >
              <Check className="h-3 w-3" aria-hidden />
              Seen
            </span>
          ) : null}
          <ScoreChip score={score100} showCheck />
          <button
            type="button"
            className="rounded-[6px] p-1 text-[color:var(--vscode-text-tertiary)] opacity-0 transition-opacity hover:text-[color:var(--vscode-text-primary)] group-hover:opacity-100 focus:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(m);
            }}
            aria-label="Open candidate detail"
            title="Open candidate detail"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      {/*
       * N×M context line (21-Apr sample image) — when the card is
       * scored against a specific JD × client, surface them so the
       * user can answer "fit for this role for this client?" at a
       * glance even with grouping collapsed. Hidden on legacy rows
       * without a job / client (prompt-only searches).
       */}
      {hasJobContext ? (
        <p
          className="truncate text-[11px]"
          style={{ color: 'var(--vscode-text-secondary)' }}
          title={`${jobTitle} · ${clientName}`}
        >
          <span className="font-medium">{jobTitle}</span>
          <span className="mx-1" style={{ color: 'var(--vscode-text-tertiary)' }}>
            ·
          </span>
          <span>{clientName}</span>
        </p>
      ) : null}

      {/*
       * Bolt-style fact strip (21-Apr-26 visual refresh). Experience,
       * current company, location, and remote preference now read as
       * compact icon rows rather than grid cells — much closer to the
       * recommended-companies card the user referenced.
       */}
      {facts.length > 0 ? (
        <div className="flex flex-col gap-0.5">
          {facts.map((f) => (
            <Fact key={f.key} Icon={f.Icon} text={f.text} title={f.title} />
          ))}
        </div>
      ) : null}

      {/*
       * Matched / missing skill chips. Matched render emerald-soft
       * (the house accent), missing render muted red-soft so the gap
       * is visible at a glance without shouting. `+N` affordance when
       * the underlying array is longer than the slice shown.
       */}
      {matchedSkills.length > 0 || missingSkills.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {matchedSkills.map((s) => (
            <span
              key={`m-${s}`}
              className="inline-flex h-5 items-center rounded-full px-2 text-[10px] font-medium"
              style={{
                background: '#E0F2FE',
                color: '#0369A1',
              }}
              title={`Matched skill · ${s}`}
            >
              {s}
            </span>
          ))}
          {(m.matched_skills ?? []).length > matchedSkills.length ? (
            <span
              className="inline-flex h-5 items-center rounded-full px-2 text-[10px] font-medium"
              style={{
                background: 'var(--vscode-bg-hover)',
                color: 'var(--vscode-text-tertiary)',
              }}
              title={`${(m.matched_skills ?? []).length - matchedSkills.length} more matched skills`}
            >
              +{(m.matched_skills ?? []).length - matchedSkills.length}
            </span>
          ) : null}
          {missingSkills.map((s) => (
            <span
              key={`x-${s}`}
              className="inline-flex h-5 items-center gap-0.5 rounded-full px-2 text-[10px] font-medium"
              style={{
                background: '#F1F5F9',
                color: '#64748B',
              }}
              title={`Missing skill · ${s}`}
            >
              {s}
            </span>
          ))}
        </div>
      ) : null}

      {/*
       * Recommendation (21-Apr scope update) — the user asked for the
       * card to answer "what other jobs/clients is this candidate
       * suitable for?", not the inference verdict string. We surface
       * the top alternate matches from the N×M result set, sorted by
       * score, so the recruiter can spot cross-role reuse without
       * leaving the sourcing column. Hidden when there are no
       * alternates (single-JD flow, hidden tab).
       */}
      {alternateMatches.length > 0 ? (
        <div>
          <p
            className="mb-1 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Also a fit for
          </p>
          <ul className="flex flex-col gap-1">
            {alternateMatches.map((alt) => {
              const altRaw = Number.isFinite(alt.composite_score)
                ? Number(alt.composite_score)
                : 0;
              const altScore = Math.round(
                Math.max(
                  0,
                  Math.min(100, altRaw > 1 ? altRaw : altRaw * 100),
                ),
              );
              const altJob = jobTitleFromMatch(alt);
              const altClient = clientNameFromMatch(alt);
              const altBandColor = BAND_LABEL[bandOf(altScore)].color;
              const altJobId =
                alt.job_id && alt.job_id !== '__prompt__' ? alt.job_id : null;
              // Only wrap in a Link when we have a real jobs/:id target.
              // Synthetic "__prompt__" rows (single-JD fallback without a
              // persisted job) stay non-navigable so we don't dead-end
              // the user on a 404. Click bubbles stopped so the parent
              // card's focus handler doesn't fire under the link.
              const inner = (
                <>
                  <span
                    className="truncate"
                    style={{ color: 'var(--vscode-text-secondary)' }}
                  >
                    <span className="font-medium">{altJob}</span>
                    <span
                      className="mx-1"
                      style={{ color: 'var(--vscode-text-tertiary)' }}
                    >
                      ·
                    </span>
                    <span style={{ color: 'var(--vscode-text-tertiary)' }}>
                      {altClient}
                    </span>
                    {altJobId ? (
                      <ExternalLink
                        className="ml-1 inline h-3 w-3 align-[-2px]"
                        style={{ color: 'var(--vscode-text-tertiary)' }}
                        aria-hidden
                      />
                    ) : null}
                  </span>
                  <span
                    className={`inline-flex h-4 min-w-[30px] shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${altBandColor}`}
                    style={{ background: 'var(--vscode-bg-hover)' }}
                  >
                    {altScore}%
                  </span>
                </>
              );
              const key = alt.id ?? `${alt.candidate_id}-${alt.job_id}`;
              const rowCls =
                'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded px-1 -mx-1 text-[12px]';
              return (
                <li key={key} title={`${altJob} · ${altClient} — ${altScore}%`}>
                  {altJobId ? (
                    <Link
                      to={`/jobs/${altJobId}`}
                      onClick={(e) => e.stopPropagation()}
                      className={`${rowCls} transition-colors hover:bg-[var(--vscode-bg-hover)]`}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className={rowCls}>{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {sources.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {sources.map(({ key, label, Icon }) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: 'var(--vscode-text-tertiary)',
              }}
            >
              <Icon className="h-3 w-3" aria-hidden />
              {label}
            </span>
          ))}
          {/* bandLabel is rendered next to the name above — no need to
              duplicate it in the sources strip. */}
        </div>
      ) : null}

      <div
        className="-mx-3 -mb-3 flex items-center justify-between gap-2 border-t px-3 py-1.5"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        {variant === 'sourcing' ? (
          <>
            <button
              type="button"
              disabled={hideBusy}
              onClick={(e) => {
                e.stopPropagation();
                onHide?.(m);
              }}
              className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[color:color-mix(in_srgb,#EF4444_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                borderColor: 'color-mix(in srgb, #EF4444 55%, transparent)',
                color: '#DC2626',
              }}
              aria-label="Hide candidate"
            >
              {hideBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <EyeOff className="h-3.5 w-3.5" aria-hidden />
              )}
              <span>Hide</span>
            </button>
            <div className="relative">
              <button
                type="button"
                disabled={shortlistBusy || shortlisted}
                onClick={onPrimaryActionClick}
                className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-[var(--ggj-accent,#10B981)] px-3 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label={`${shortlistLabelFor(collection)} candidate`}
                aria-haspopup={shouldOpenCollectionsPicker(collection) ? 'menu' : undefined}
                aria-expanded={pickerOpen}
              >
                {shortlistBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : shortlisted ? (
                  <Check className="h-3.5 w-3.5" aria-hidden />
                ) : null}
                <span>
                  {shortlisted
                    ? `${shortlistLabelFor(collection)}d`
                    : shortlistLabelFor(collection)}
                </span>
                {!shortlisted ? (
                  <ArrowRight className="h-3 w-3" aria-hidden />
                ) : null}
              </button>
              <CollectionsPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={(col) => {
                  setPickerOpen(false);
                  onAttachCollection?.(col);
                  // Fire the shortlist immediately — the parent's save flow
                  // owns the busy/optimistic update.
                  onShortlist?.(m);
                }}
              />
            </div>
          </>
        ) : variant === 'shortlisted' ? (
          <>
            <button
              type="button"
              disabled={hideBusy}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsSeen?.(m);
              }}
              className={`inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                viewed
                  ? 'hover:bg-[var(--ggj-accent-soft,#D1FAE5)]'
                  : 'hover:bg-[var(--vscode-bg-hover)]'
              }`}
              style={{
                borderColor: viewed
                  ? 'var(--ggj-accent,#10B981)'
                  : 'var(--vscode-border-primary)',
                color: viewed ? '#065F46' : 'var(--vscode-text-secondary)',
                background: viewed
                  ? 'var(--ggj-accent-soft,#D1FAE5)'
                  : undefined,
              }}
              aria-pressed={viewed}
              aria-label={viewed ? 'Mark as unseen' : 'Mark as seen'}
            >
              {hideBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : viewed ? (
                <EyeOff className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <Eye className="h-3.5 w-3.5" aria-hidden />
              )}
              <span>{viewed ? 'Mark as unseen' : 'Mark as seen'}</span>
            </button>
            <button
              type="button"
              disabled={removeBusy}
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(m);
              }}
              className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[color:color-mix(in_srgb,#EF4444_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                borderColor: 'color-mix(in srgb, #EF4444 55%, transparent)',
                color: '#DC2626',
              }}
              aria-label="Remove from shortlist"
            >
              {removeBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Undo2 className="h-3.5 w-3.5" aria-hidden />
              )}
              <span>Remove</span>
              <ArrowRight className="h-3 w-3" aria-hidden />
            </button>
          </>
        ) : (
          /* hidden */
          <button
            type="button"
            disabled={removeBusy}
            onClick={(e) => {
              e.stopPropagation();
              onRestore?.(m);
            }}
            className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--ggj-accent-soft,#D1FAE5)] disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              borderColor: 'var(--ggj-accent,#10B981)',
              color: '#065F46',
            }}
            aria-label="Restore candidate"
          >
            {removeBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            )}
            <span>Restore</span>
          </button>
        )}
      </div>
    </div>
  );
});
